/**
 * Carousel bootstrap and slide engine.
 *
 * Module map:
 * - carousel-layout.js    breakpoints, mobile-only .carousel class, track step px
 * - carousel-keyboard.js  tab order, arrows/bullets, Enter/Space
 * - carousel-swipe.js     touch, drag, horizontal wheel
 * - carousel-job-postings.js  shift-req animation, slide inert
 * - testimonials.js       testimonial breakpoints, track offset, dot count
 * - testimonials-edge-fade.js  side fades when scrolling testimonials
 * - carousel-peek-scroll.js    data-carousel-peek="1" (separate init path)
 *
 * Public API: initCarouselContainer, syncMobileCarousels (also used by job-postings.js).
 */

import { $, $$ } from '../utils';
import { initPeekScrollCarousel } from './carousel-peek-scroll';
import {
    applyBulletsTabFocus,
    bindCarouselBullets,
    bindDirectionalArrows,
    isRtlDocument,
    setArrowGroupState,
} from './carousel-keyboard';
import {
    CAROUSEL_CONTAINER_SELECTOR,
    FIXED_STEP_RESYNC_SELECTOR,
    getFixedStepCarouselTrackStep,
    isActiveCarousel,
    toggleMobileCarousels,
} from './carousel-layout';
import { initJobPostingShiftReq, revealShiftReq, syncJobPostingSlideInert } from './carousel-job-postings';
import { addCarouselSwipeEvents } from './carousel-swipe';
import {
    getTestimonialsCarouselTrackOffsetPx,
    getTestimonialsMaxSlideIndex,
    getTestimonialsRow,
    isTestimonialsCarousel,
    isTestimonialsEdgeFadeAtViewport,
    isTestimonialsScrollableAtViewport,
    syncTestimonialsBulletVisibility,
} from './testimonials';
import { createTestimonialsEdgeFade } from './testimonials-edge-fade';

const refreshState = () => {
    $$(CAROUSEL_CONTAINER_SELECTOR).forEach((container) => {
        const slidesWrap = container.querySelector('.carousel__slides');
        if (!slidesWrap) return;

        const slides = slidesWrap.querySelectorAll('.carousel__slide');
        const active = isActiveCarousel(container);

        slidesWrap.style.cursor = active && slides.length > 1 ? 'grab' : 'default';

        if (!active) {
            slides.forEach((slide, index) => {
                slide.style.transform = '';
                slide.classList.toggle('is-active', index === 0);
            });
        }

        container._syncCarouselNavFocus?.();

        if (container.getAttribute('data-carousel-peek') === '1') {
            container._peekScrollRefresh?.();
        }
    });
};

export const initCarouselContainer = (container) => {
    if (!container) return;

    if (container.getAttribute('data-carousel-peek') === '1') {
        container._peekScrollRefresh = initPeekScrollCarousel(container, isActiveCarousel);
        return;
    }

    const isJobPostings = container.matches(
        '#job-postings-row .carousel--mobile-only, .job-posting-cards .carousel--mobile-only',
    );
    const testimonialsRow = getTestimonialsRow(container);
    const isTestimonials = isTestimonialsCarousel(container);
    const isTestimonialsScrollable = () => isTestimonialsScrollableAtViewport(testimonialsRow);
    const usesFixedStep = () => isJobPostings || isTestimonialsScrollable();

    if (isJobPostings) initJobPostingShiftReq(container);

    const track = container.querySelector('.carousel__slides');
    if (!track) return;

    const slides = [...track.querySelectorAll('.carousel__slide')];
    const navEnabled = () => isActiveCarousel(container) && slides.length > 1;
    const bullets = [...container.querySelectorAll('.carousel__nav .carousel__bullet')];
    const navRoot = container.closest('#testimonials-row') ?? container;
    const prevArrows = [...navRoot.querySelectorAll('.carousel__arrow--prev')];
    const nextArrows = [...navRoot.querySelectorAll('.carousel__arrow--next')];
    const currentNumber = container.querySelector('.carousel__numbers__current');
    const isRtl = isRtlDocument();
    const relatedRoot = container.getAttribute('data-depends') ? $(container.getAttribute('data-depends')) : null;
    const relatedSlides = relatedRoot?.querySelectorAll('.carousel__slide') ?? null;

    if (slides.length <= 1) track.style.cursor = 'default';

    const getActiveIndex = () =>
        Math.max(
            0,
            slides.findIndex((slide) => slide.classList.contains('is-active')),
        );

    const getMaxSlideIndex = () =>
        isTestimonialsScrollable() ? getTestimonialsMaxSlideIndex(slides.length, testimonialsRow) : slides.length - 1;

    const getDirection = (delta) => (delta > 0 ? (isRtl ? 1 : -1) : isRtl ? -1 : 1);

    const syncNavFocus = (index = getActiveIndex()) => {
        if (isTestimonials) syncTestimonialsBulletVisibility(bullets, slides.length, testimonialsRow);
        applyBulletsTabFocus(bullets, navEnabled());
        setArrowGroupState(prevArrows, index > 0);
        setArrowGroupState(nextArrows, index < getMaxSlideIndex());
        if (isJobPostings) syncJobPostingSlideInert(slides, navEnabled());
    };

    container._syncCarouselNavFocus = syncNavFocus;

    const testimonialsEdgeFade = isTestimonials
        ? createTestimonialsEdgeFade({
              container,
              slides,
              isRtl,
              getActiveIndex,
              getMaxSlideIndex,
              isScrollable: isTestimonialsScrollable,
              isEdgeFadeEnabled: isTestimonialsEdgeFadeAtViewport,
          })
        : null;

    const applyTrackTransform = (targetIndex) => {
        let trackOffsetPx = null;
        if (usesFixedStep()) {
            trackOffsetPx = isTestimonialsScrollable()
                ? getTestimonialsCarouselTrackOffsetPx(track, slides, targetIndex, testimonialsRow)
                : targetIndex * getFixedStepCarouselTrackStep(track, slides);
        }

        const trackTransform =
            trackOffsetPx === null
                ? `translateX(${targetIndex * (isRtl ? 100 : -100)}%)`
                : `translateX(${isRtl ? trackOffsetPx : -trackOffsetPx}px)`;

        slides.forEach((slide, index) => {
            slide.style.transform = trackTransform;
            slide.classList.toggle('is-active', index === targetIndex);
            const related = relatedSlides?.[index];
            if (related) {
                related.style.transform = trackTransform;
                related.classList.toggle('is-active', index === targetIndex);
            }
        });

        bullets.forEach((bullet, index) => bullet.classList.toggle('is-active', index === targetIndex));
    };

    const changeSlide = (value, isIndex = false) => {
        if (!isActiveCarousel(container)) return;
        if (isTestimonials && !isTestimonialsScrollable()) return;

        const currentIndex = getActiveIndex();
        const targetIndex = Math.min(Math.max(isIndex ? value : currentIndex + value, 0), getMaxSlideIndex());

        testimonialsEdgeFade?.hideImmediateAtBounds(targetIndex);
        applyTrackTransform(targetIndex);
        syncNavFocus(targetIndex);
        testimonialsEdgeFade?.animateWithSlide(currentIndex, targetIndex);
        if (currentNumber) currentNumber.textContent = targetIndex + 1;
        revealShiftReq(container, slides[targetIndex]);
    };

    const swipeOpts = {
        isActive: () => isActiveCarousel(container),
        getDirection,
        changeSlide,
    };

    addCarouselSwipeEvents(track, swipeOpts);
    if (relatedRoot) addCarouselSwipeEvents(relatedRoot, swipeOpts);
    bindDirectionalArrows(prevArrows, -1, changeSlide);
    bindDirectionalArrows(nextArrows, 1, changeSlide);
    bindCarouselBullets(bullets, changeSlide);

    const resetTestimonialsStaticLayout = () => {
        slides.forEach((slide, index) => {
            slide.style.transform = '';
            slide.classList.toggle('is-active', index === 0);
        });
        bullets.forEach((bullet, index) => bullet.classList.toggle('is-active', index === 0));
        syncNavFocus(0);
        track.style.cursor = 'default';
        testimonialsEdgeFade?.sync();
    };

    if (isTestimonials || isJobPostings) {
        container._syncFixedStepCarouselSlide = () => {
            if (isJobPostings && !isActiveCarousel(container)) return;
            if (isTestimonialsScrollable()) changeSlide(getActiveIndex(), true);
            else if (isTestimonials) resetTestimonialsStaticLayout();
            else changeSlide(getActiveIndex(), true);
        };
    }

    if (isTestimonials && !isTestimonialsScrollable()) resetTestimonialsStaticLayout();
    else if (isActiveCarousel(container)) changeSlide(getActiveIndex(), true);
    else syncNavFocus();
};

export const syncMobileCarousels = () => {
    toggleMobileCarousels();
    refreshState();
    $$(FIXED_STEP_RESYNC_SELECTOR).forEach((container) => {
        container._syncFixedStepCarouselSlide?.();
    });
};

const initAllCarousels = () => {
    toggleMobileCarousels();
    $$(CAROUSEL_CONTAINER_SELECTOR).forEach(initCarouselContainer);
    refreshState();
};

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAllCarousels);
} else {
    initAllCarousels();
}

window.addEventListener('resize', syncMobileCarousels);
